/**
 * Orkestrator E4 §10 — client hooks for the workspace behaviour settings
 * (`GET|PATCH /api/workspace/:id/settings`) and the "undelivered notifications" read
 * (peer B, E4 §3: `GET /api/notifications/undelivered`; absent → nothing is shown).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '#/lib/api'
import type { WorkspaceSettingsDto, WorkspaceSettingsPatchInput } from '#/lib/workspace-settings'

export interface WorkspaceSettingsResponse {
  workspace: WorkspaceSettingsDto
  /** additive: only the owner may change these settings */
  canEdit?: boolean
}

export const workspaceSettingsKey = (workspaceId: string) => ['workspace-settings', workspaceId] as const

export function useWorkspaceSettings(workspaceId: string) {
  return useQuery({
    queryKey: workspaceSettingsKey(workspaceId),
    queryFn: () => api<WorkspaceSettingsResponse>(`/api/workspace/${workspaceId}/settings`),
    enabled: Boolean(workspaceId),
    retry: false,
  })
}

export function useUpdateWorkspaceSettings(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: WorkspaceSettingsPatchInput) => api<WorkspaceSettingsResponse>(`/api/workspace/${workspaceId}/settings`, { method: 'PATCH', json: patch }),
    // optimistic: the chosen option shows at once; a refused write (e.g. 403) rolls back
    onMutate: async (patch) => {
      const key = workspaceSettingsKey(workspaceId)
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<WorkspaceSettingsResponse>(key)
      if (previous) qc.setQueryData<WorkspaceSettingsResponse>(key, { ...previous, workspace: { ...previous.workspace, ...patch } })
      return { previous }
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) qc.setQueryData(workspaceSettingsKey(workspaceId), context.previous)
    },
    onSuccess: (data) => {
      qc.setQueryData(workspaceSettingsKey(workspaceId), data)
      // the Inbox "continue elsewhere" offer depends on fallbackPolicy
      void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes('inbox') })
    },
  })
}

export type UndeliveredReason = 'rate_limited' | 'no_channel' | 'disabled'
export interface UndeliveredSummary {
  total: number
  byReason: Partial<Record<UndeliveredReason, number>>
  recent: { at: string; event: string; reason: string }[]
}

const isSummary = (x: unknown): x is UndeliveredSummary =>
  typeof x === 'object' && x !== null && typeof (x as { total?: unknown }).total === 'number'

/** Never throws: a missing endpoint (404 until peer B lands), a non-JSON answer or any error → null (nothing shown). */
export function useUndeliveredNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications', 'undelivered'] as const,
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<UndeliveredSummary | null> => {
      try {
        const data = await api<unknown>('/api/notifications/undelivered')
        return isSummary(data) ? { total: data.total, byReason: data.byReason ?? {}, recent: Array.isArray(data.recent) ? data.recent : [] } : null
      } catch {
        return null
      }
    },
  })
}
